import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const ContextMenu = ({ anchorRect, x, y, visible, onClose, menuItems }) => {
    const menuRef = useRef(null);
    const [position, setPosition] = useState({ left: 0, top: 0 });

    useLayoutEffect(() => {
        const hasPoint = Number.isFinite(x) && Number.isFinite(y);
        const resolvedAnchorRect = anchorRect || (hasPoint
            ? {
                left: x - window.scrollX,
                top: y - window.scrollY,
                bottom: y - window.scrollY,
                width: 0
            }
            : null);

        if (!visible || !resolvedAnchorRect) {
            return;
        }
        const menu = menuRef.current;
        if (!menu) {
            return;
        }

        const margin = 8;
        const menuRect = menu.getBoundingClientRect();

        // Position directly below the anchor element, left-aligned
        let nextLeft = resolvedAnchorRect.left;
        let nextTop = (Number.isFinite(resolvedAnchorRect.bottom) ? resolvedAnchorRect.bottom : resolvedAnchorRect.top) + 4; // 4px gap below the element

        // Keep within viewport bounds
        if (nextLeft + menuRect.width > window.innerWidth - margin) {
            nextLeft = window.innerWidth - menuRect.width - margin;
        }
        if (nextTop + menuRect.height > window.innerHeight - margin) {
            // If no room below, show above the element instead
            nextTop = resolvedAnchorRect.top - menuRect.height - 4;
        }

        nextLeft = Math.max(margin, nextLeft);
        nextTop = Math.max(margin, nextTop);
        setPosition({ left: nextLeft, top: nextTop });
    }, [anchorRect, x, y, visible, menuItems]);

    useEffect(() => {
        if (!visible) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                onClose();
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown, { passive: true });
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, visible]);

    if (!visible) return null;

    return ReactDOM.createPortal(
        <div
            ref={menuRef}
            style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '4px 0',
                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                zIndex: 2147483647,
                color: 'black'
            }}
        >
            {menuItems.map((item, index) => (
                <div
                    key={index}
                    onClick={async (e) => {
                        // Prevent accidental bubbling to underlying UI and
                        // make sure async handlers complete before closing.
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                            if (item.onClick) {
                                await Promise.resolve(item.onClick(e));
                            }
                        } finally {
                            onClose();
                        }
                    }}
                    style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        textAlign: 'left'
                    }}
                >
                    {item.label}
                </div>
            ))}
        </div>,
        document.body
    );
};

export default React.memo(ContextMenu);
